import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Sparkles, Dog, PawPrint, ArrowRight, HomeIcon, X, Heart } from "lucide-react";

const WelcomeView = ({ previousPets = [], onSelectPetView, handleNewIntegrant, onClose }) => {
    // Track active pet for better user feedback
    const [activePet, setActivePet] = useState(null);
    // Track hover states for better animation control
    const [hoveredPetId, setHoveredPetId] = useState(null);
    
    const handlePetSelect = (pet) => {
        setActivePet(pet);
        onSelectPetView(pet);
    };

    // Enhanced animation variants
    const containerVariants = {
        hidden: { opacity: 0, scale: 0.98 },
        visible: { 
            opacity: 1, 
            scale: 1,
            transition: { 
                duration: 0.3,
                ease: "easeOut",
                when: "beforeChildren",
                staggerChildren: 0.1
            }
        },
        exit: { opacity: 0, scale: 0.98, transition: { duration: 0.2 } }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
    };

    return (
        <motion.div
            key="welcome-view"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="relative bg-gradient-to-b from-amber-50 to-white rounded-2xl shadow-lg border border-amber-100/50 overflow-hidden
                     sm:rounded-xl md:rounded-2xl max-w-[95vw] sm:max-w-[85vw] w-full mx-auto"
            style={{ maxHeight: '90vh', maxWidth: '1000px' }}
        >
            {/* Enhanced Mobile-First Header Section */}
            <div className="sticky top-0 z-10 px-4 py-5 sm:p-6 text-center border-b bg-gradient-to-r from-amber-100/70 via-amber-50/70 to-amber-100/70 backdrop-blur-sm">
                {/* Top decoration bar - Enhanced glow effect */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 sm:w-24 h-1 sm:h-1.5 bg-gradient-to-r from-amber-300 to-amber-500 rounded-b-lg" 
                     style={{ boxShadow: '0 1px 3px rgba(242, 169, 59, 0.2)' }} />

                {/* Close button with enhanced hover effect */}
                {onClose && (
                    <motion.button 
                        onClick={onClose}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/90 backdrop-blur-sm shadow-sm border border-amber-100 hover:bg-white transition-all duration-200 active:bg-amber-50"
                    >
                        <X className="w-4 h-4 text-amber-800" />
                    </motion.button>
                )}

                <div className="mx-auto mb-4 relative">
                    {/* Enhanced dog logo animation */}
                    <motion.div
                        initial={{ rotate: 0 }}
                        animate={{ 
                            rotate: [0, 6, 0, -6, 0],
                            scale: [1, 1.02, 1, 1.02, 1]
                        }}
                        transition={{ 
                            duration: 6, 
                            repeat: Infinity,
                            repeatType: "loop"
                        }}
                        className="w-14 h-14 sm:w-16 sm:h-16 mx-auto bg-gradient-to-br from-amber-400 to-amber-500 rounded-2xl shadow-lg flex items-center justify-center border-2 border-amber-300/20"
                        style={{ boxShadow: '0 8px 20px -6px rgba(242, 169, 59, 0.25)' }}
                    >
                        <Dog className="w-8 h-8 sm:w-10 sm:h-10 text-white drop-shadow-sm" />
                    </motion.div>
                    
                    {/* Enhanced floating paw prints */}
                    <motion.div
                        animate={{ 
                            y: [0, -6, 0],
                            opacity: [1, 0.7, 1],
                            scale: [1, 0.95, 1]
                        }}
                        transition={{ duration: 3, repeat: Infinity }}
                        className="absolute -bottom-2 right-1/2 transform translate-x-10"
                    >
                        <PawPrint className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600/70" />
                    </motion.div>
                </div>

                {/* Enhanced text animation */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="space-y-2"
                >
                    <h2 className="text-xl sm:text-2xl font-bold text-amber-900 drop-shadow-sm">
                        ¿Qué peludito vamos a revisar?
                    </h2>
                    <p className="text-sm sm:text-base text-amber-700/90 max-w-md mx-auto">
                        Selecciona un miembro de tu familia o comienza un análisis
                    </p>
                </motion.div>
            </div>

            {/* Enhanced Mobile-First Scrollable Content */}
            <div className="overflow-y-auto px-3 py-4 sm:p-6 scrollbar-thin scrollbar-thumb-amber-200 scrollbar-track-transparent" 
                 style={{ maxHeight: 'calc(90vh - 180px)' }}>
                <motion.div 
                    variants={itemVariants}
                    className="space-y-4 sm:space-y-6"
                >
                    {/* Enhanced Previous Pets Grid */}
                    {previousPets.length > 0 && (
                        <motion.div variants={itemVariants} className="space-y-3">
                            <div className="flex items-center justify-between px-1">
                                <h3 className="text-base sm:text-lg font-medium text-amber-900 flex items-center gap-2">
                                    <HomeIcon className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                                    <span className="relative">
                                        Tu familia
                                        <motion.div 
                                            className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-amber-300/80 to-transparent rounded-full"
                                            initial={{ scaleX: 0 }}
                                            animate={{ scaleX: 1 }}
                                            transition={{ delay: 0.5, duration: 0.8 }}
                                        />
                                    </span>
                                </h3>
                                <span className="text-xs sm:text-sm text-amber-600 bg-amber-100/60 px-2.5 py-1 rounded-full border border-amber-200/40">
                                    Toca para elegir
                                </span>
                            </div>
                            
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5 sm:gap-4">
                                {previousPets.map((pet, index) => {
                                    const uniqueKey = `pet-${pet.id || pet._id || index}-${pet.name}-${index}`;
                                    const isActive = pet === activePet;
                                    const isHovered = hoveredPetId === uniqueKey;

                                    return (
                                        <motion.div
                                            key={uniqueKey}
                                            variants={itemVariants}
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={() => handlePetSelect(pet)}
                                            onHoverStart={() => setHoveredPetId(uniqueKey)}
                                            onHoverEnd={() => setHoveredPetId(null)}
                                            className={`relative w-full aspect-square rounded-xl ${
                                                isActive 
                                                    ? 'ring-2 ring-amber-400 shadow-lg' 
                                                    : 'border border-amber-100/80'
                                            } hover:border-amber-300 bg-white transition-all duration-200 group overflow-hidden`}
                                            style={{ 
                                                boxShadow: isActive ? '0 4px 12px rgba(242, 169, 59, 0.15)' : '',
                                                transform: isActive ? 'translateY(-1px)' : ''
                                            }}
                                        >
                                            <div className="absolute inset-0 p-1">
                                                <div className="relative w-full h-full rounded-lg overflow-hidden bg-amber-50">
                                                    {pet.image ? (
                                                        <img
                                                            src={pet.image}
                                                            alt={pet.name}
                                                            className="w-full h-full object-cover"
                                                            loading="lazy"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full bg-gradient-to-br from-amber-100 to-amber-50 flex items-center justify-center">
                                                            <Dog className="w-1/3 h-1/3 text-amber-500" />
                                                        </div>
                                                    )}

                                                    {/* Enhanced name overlay */}
                                                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent flex items-end p-2">
                                                        <span className="text-xs sm:text-sm font-medium text-white drop-shadow">
                                                            {pet.name}
                                                        </span>
                                                    </div>

                                                    {/* Enhanced selection indicator */}
                                                    <motion.div
                                                        animate={{
                                                            scale: isHovered ? 1.1 : 1,
                                                            rotate: isHovered ? 15 : 0
                                                        }}
                                                        className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-amber-400/90 flex items-center justify-center shadow-sm"
                                                    >
                                                        <Heart className={`w-3 h-3 ${isActive ? 'text-white fill-white' : 'text-white'}`} />
                                                    </motion.div>

                                                    {/* Enhanced hover effect */}
                                                    <motion.div
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: isHovered ? 1 : 0 }}
                                                        className="absolute inset-0 bg-gradient-to-t from-amber-500/40 to-transparent"
                                                    />
                                                </div>
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        </motion.div>
                    )}

                    {/* Enhanced Mobile-First Actions Grid */}
                    <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {/* Enhanced New Pet Button */}
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={handleNewIntegrant}
                            className="relative flex items-center gap-3 p-4 rounded-xl border-2 border-amber-200 hover:border-amber-400 
                                     bg-gradient-to-br from-white to-amber-50/60 transition-all duration-200 group shadow-sm hover:shadow-md"
                        >
                            <div className="p-2.5 rounded-xl bg-amber-100 group-hover:bg-amber-200/70 transition-colors duration-200">
                                <Plus className="w-5 h-5 text-amber-600" />
                            </div>
                            <div className="flex-1">
                                <h3 className="font-semibold text-amber-900 text-base">Nuevo integrante</h3>
                                <p className="text-xs text-amber-600">Crear perfil</p>
                            </div>
                            <motion.div
                                animate={{ x: [0, 3, 0] }}
                                transition={{ duration: 2, repeat: Infinity }}
                            >
                                <ArrowRight className="w-5 h-5 text-amber-500" />
                            </motion.div>
                        </motion.button>

                        {/* Enhanced Quick Analysis Button */}
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => onSelectPetView('')}
                            className="relative flex items-center gap-3 p-4 rounded-xl border border-gray-100 hover:border-amber-300 
                                     bg-white hover:bg-amber-50/30 transition-all duration-200 group shadow-sm hover:shadow-md"
                        >
                            <div className="p-2.5 rounded-xl bg-gray-100 group-hover:bg-amber-100/70 transition-colors duration-200">
                                <Sparkles className="w-5 h-5 text-gray-500 group-hover:text-amber-600 transition-colors duration-200" />
                            </div>
                            <div className="flex-1">
                                <h3 className="font-semibold text-gray-700 group-hover:text-gray-800 text-base transition-colors duration-200">
                                    Análisis rápido
                                </h3>
                                <p className="text-xs text-gray-500 group-hover:text-amber-600 transition-colors duration-200">
                                    Sin perfil
                                </p>
                            </div>
                            <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-amber-500 transition-colors duration-200" />
                        </motion.button>
                    </motion.div>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default WelcomeView;